import { create } from 'zustand';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../services/firebase';
import { COLLECTIONS } from '../services/firestore';

const sanitizeFilename = (name = 'asset') =>
  name.replace(/[^a-zA-Z0-9._-]/g, '_');

const guideAssetPath = (userId, mediaType, fileName) =>
  `guide-media/${userId}/${mediaType.toLowerCase()}-${Date.now()}-${sanitizeFilename(fileName)}`;

const normalizeGuide = (snapshot) => {
  const data = snapshot.data();
  return {
    id: snapshot.id,
    ...data,
    mediaTypes: Array.isArray(data.mediaTypes) ? data.mediaTypes : [],
    useCases: Array.isArray(data.useCases) ? data.useCases : [],
    createdAt: data.createdAt?.toDate?.() || new Date(),
    updatedAt: data.updatedAt?.toDate?.() || new Date(),
  };
};

const sortGuides = (guides = []) =>
  [...guides].sort((a, b) => {
    const featureCompare = String(a.feature || '').localeCompare(String(b.feature || ''));
    if (featureCompare !== 0) return featureCompare;
    return String(a.title || '').localeCompare(String(b.title || ''));
  });

export const useGuideStore = create((set, get) => ({
  guides: [],
  isLoading: false,
  error: null,
  hasLoaded: false,

  loadGuides: async ({ force = false } = {}) => {
    if (get().hasLoaded && !force) return get().guides;

    try {
      set({ isLoading: true, error: null });
      const snapshot = await getDocs(collection(db, COLLECTIONS.GUIDES));
      const guides = sortGuides(snapshot.docs.map(normalizeGuide));
      set({ guides, isLoading: false, error: null, hasLoaded: true });
      return guides;
    } catch (error) {
      console.error('Error loading guides:', error);
      set({ isLoading: false, error: error.message || 'Failed to load guides.' });
      throw error;
    }
  },

  createGuide: async (guideData) => {
    try {
      set({ error: null });
      const docRef = await addDoc(collection(db, COLLECTIONS.GUIDES), {
        ...guideData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const createdGuide = {
        id: docRef.id,
        ...guideData,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      set((state) => ({
        guides: sortGuides([createdGuide, ...state.guides]),
      }));

      return createdGuide;
    } catch (error) {
      console.error('Error creating guide:', error);
      set({ error: error.message || 'Failed to create guide.' });
      throw error;
    }
  },

  updateGuide: async (guideId, updates) => {
    try {
      set({ error: null });
      await updateDoc(doc(db, COLLECTIONS.GUIDES, guideId), {
        ...updates,
        updatedAt: serverTimestamp(),
      });

      set((state) => ({
        guides: sortGuides(
          state.guides.map((guide) =>
            guide.id === guideId
              ? {
                  ...guide,
                  ...updates,
                  updatedAt: new Date(),
                }
              : guide
          )
        ),
      }));
    } catch (error) {
      console.error('Error updating guide:', error);
      set({ error: error.message || 'Failed to update guide.' });
      throw error;
    }
  },

  deleteGuide: async (guideId) => {
    try {
      set({ error: null });
      const guide = get().guides.find((entry) => entry.id === guideId);
      const paths = [guide?.imagePath, guide?.documentPath, guide?.audioPath].filter(Boolean);
      await Promise.all(paths.map((path) => deleteObject(ref(storage, path)).catch(() => {})));
      await deleteDoc(doc(db, COLLECTIONS.GUIDES, guideId));
      set((state) => ({
        guides: state.guides.filter((guide) => guide.id !== guideId),
      }));
    } catch (error) {
      console.error('Error deleting guide:', error);
      set({ error: error.message || 'Failed to delete guide.' });
      throw error;
    }
  },

  uploadGuideAsset: async ({ file, userId, mediaType, existingPath = null }) => {
    if (!file) throw new Error(`No ${mediaType.toLowerCase()} file selected.`);
    if (!userId) throw new Error('Missing authenticated user.');

    const path = guideAssetPath(userId, mediaType, file.name || mediaType.toLowerCase());
    const fileRef = ref(storage, path);
    await uploadBytes(fileRef, file, { contentType: file.type || 'application/octet-stream' });
    const url = await getDownloadURL(fileRef);

    if (existingPath && existingPath !== path) {
      await deleteObject(ref(storage, existingPath)).catch(() => {});
    }

    return {
      path,
      url,
      name: file.name || sanitizeFilename(mediaType.toLowerCase()),
      contentType: file.type || 'application/octet-stream',
      size: file.size || 0,
      uploadedAt: new Date().toISOString(),
    };
  },

  deleteGuideAsset: async (path = null) => {
    if (!path) return;
    await deleteObject(ref(storage, path)).catch(() => {});
  },

  clearGuideError: () => set({ error: null }),
}));
